<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { toast } from '@/components/ui/toast'
import {
  fetchMonitoringSettings,
  updateMonitoringSettings,
} from '@/services/monitoringApi'

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
})

const store = useStore()

const orgId = props.project.organization_id
const projectId = props.project.project_id

const settings = ref(null)
const loading = ref(true)

const ALERT_LINES = [
  { key: 'water', label: 'Water stress' },
  { key: 'heat', label: 'Heat stress' },
  { key: 'vegetation', label: 'Vegetation decline' },
  { key: 'carbon', label: 'Carbon & canopy' },
  { key: 'imagery', label: 'New imagery' },
]

const catalog = computed(() => settings.value?.catalog || null)

const capitalize = (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : ''
const periodLabel = (period) => period === 'as_it_happens' ? 'As it happens' : capitalize(period)

let saveChain = Promise.resolve()
const save = (update) => {
  saveChain = saveChain.then(async () => {
    try {
      settings.value = await updateMonitoringSettings(orgId, projectId, update)
    } catch (error) {
      toast({
        title: 'Failed to save monitoring settings',
        description: error.response?.data?.detail || error.message,
        variant: 'destructive',
      })
      // Roll the optimistic UI back to what the server actually has.
      try {
        settings.value = await fetchMonitoringSettings(orgId, projectId)
      } catch (e) {
        // keep the local state; the next interaction retries
      }
    }
  })
  return saveChain
}

const toggleEnabled = async (enabled) => {
  settings.value.enabled = enabled
  await save({ enabled })
  store.dispatch('project/fetchProject', projectId)
}

const cadenceOptions = () => catalog.value?.imagery_cadences || []

const cadenceIndex = () => Math.max(0, cadenceOptions().indexOf(settings.value?.cadences?.imagery))

const pickCadence = (index) => {
  const cadence = cadenceOptions()[index]
  if (!cadence || settings.value.cadences.imagery === cadence) return
  settings.value.cadences.imagery = cadence
  save({ cadences: { imagery: cadence } })
}

const alertPref = (key) => settings.value?.alerts?.[key] || { enabled: false, period: null }
const alertPeriods = (key) => catalog.value?.alert_periods?.[key] || []

const toggleAlert = (key, enabled) => {
  const pref = { ...alertPref(key), enabled }
  settings.value.alerts[key] = pref
  save({ alerts: { [key]: pref } })
}

const pickAlertPeriod = (key, period) => {
  if (alertPref(key).period === period) return
  const pref = { ...alertPref(key), period }
  settings.value.alerts[key] = pref
  save({ alerts: { [key]: pref } })
}

onMounted(async () => {
  try {
    settings.value = await fetchMonitoringSettings(orgId, projectId)
  } catch (error) {
    toast({
      title: 'Failed to load monitoring settings',
      description: error.response?.data?.detail || error.message,
      variant: 'destructive',
    })
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div v-if="settings" class="space-y-6">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-sm font-medium">Monitoring</h3>
        <p class="text-sm text-muted-foreground">Applies to all monitored areas in this project</p>
      </div>
      <Switch :model-value="settings.enabled" @update:model-value="toggleEnabled" />
    </div>

    <div>
      <h3 class="text-sm font-medium">Imagery</h3>
      <p class="text-sm text-muted-foreground">How often new satellite images are captured for monitored areas</p>
      <div class="mt-3">
        <Slider
          :model-value="[cadenceIndex()]"
          :max="cadenceOptions().length - 1"
          :step="1"
          @update:model-value="(v) => pickCadence(v?.[0])"
        />
        <div class="mt-1.5 flex justify-between text-[11px] text-muted-foreground">
          <span
            v-for="(cadence, i) in cadenceOptions()"
            :key="cadence"
            :class="cadenceIndex() === i ? 'font-semibold text-foreground' : ''"
          >
            {{ capitalize(cadence) }}
          </span>
        </div>
      </div>
    </div>

    <div>
      <h3 class="text-sm font-medium">Alerts</h3>
      <div class="mt-2 space-y-2">
        <div
          v-for="line in ALERT_LINES"
          :key="line.key"
          class="flex items-center justify-between gap-2"
        >
          <span class="min-w-0 text-sm text-muted-foreground">{{ line.label }}</span>
          <div class="flex shrink-0 items-center gap-2">
            <div
              v-if="alertPref(line.key).enabled && alertPeriods(line.key).length > 1"
              class="flex gap-0.5 rounded-lg bg-muted p-0.5"
            >
              <button
                v-for="period in alertPeriods(line.key)"
                :key="period"
                type="button"
                class="rounded-md px-2 py-0.5 text-xs font-medium transition-colors"
                :class="alertPref(line.key).period === period ? 'bg-background shadow font-semibold' : 'text-muted-foreground hover:text-foreground'"
                @click="pickAlertPeriod(line.key, period)"
              >
                {{ periodLabel(period) }}
              </button>
            </div>
            <span
              v-else-if="alertPref(line.key).enabled"
              class="text-[11px] text-muted-foreground"
            >
              {{ periodLabel(alertPref(line.key).period || alertPeriods(line.key)[0]) }}
            </span>
            <Switch
              :model-value="alertPref(line.key).enabled"
              @update:model-value="(val) => toggleAlert(line.key, val)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <p v-else-if="loading" class="text-sm text-muted-foreground">Loading monitoring settings...</p>
</template>
